import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel, HStack, Radio, RadioGroup, Skeleton, Stack,
    VStack,
} from '@chakra-ui/react';
import z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Suspense, useEffect, useMemo, useState,
} from 'react';
import { Alert } from '@acp/core-ui';
import TinyMCEEditorWithImageUpload from '../tinymce-editor/image-upload';
import { useTenancy } from '../../features/tenancy';

const MAX_NOTES_LENGTH = 500;

const callTenantFormSchema = z.object({
    name: z.string(),
    notes: z.string(),
}).superRefine((val, ctx) => {
    if (val.notes != null) {
        let trimmed = val.notes
            .replace(/<[^>]*(>|$)|&nbsp;/g, '') // trim nbsp
            .replace(/(<([^>]+)>)/gi, '') // trim html tags
            .replace(/(?:\r\n|\r|\n)/g, ''); // trim line break

        if (trimmed.length < 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['notes'],
                message: 'Notes are required',
            });
        } else if (trimmed.length >= MAX_NOTES_LENGTH) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['notes'],
                message: `Message should be ${MAX_NOTES_LENGTH} characters or less`,
            });
        }
    }
});

const TINYMCE_CONTAINER_HEIGHT = 300;

type CallTenantFormValues = z.infer<typeof callTenantFormSchema>;

export function CallTenantForm({
    propertyId,
    onTenantCalled,
    confirmButtonText,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onTenantCalled: (notes: string) => void;
}) {
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const { data: tenancies, isLoading } = useTenancy(propertyId);

    const {
        control,
        handleSubmit,
        reset,
        formState: {
            isValid,
        },
    } = useForm<CallTenantFormValues>({
        resolver: zodResolver(callTenantFormSchema),
        mode: 'onChange',
    });

    useEffect(() => {
        if (
            tenancies === undefined
            || tenancies.active === null
            || tenancies.active.occupants.length === 0
            || tenancies.active.occupants[0].name === null) {
            return;
        }

        reset({
            name: tenancies.active.occupants[0].name,
        });
    }, [tenancies, reset]);

    const occupantContactNames = useMemo(() => {
        if (
            tenancies === undefined
            || tenancies.active === null
            || tenancies.active.occupants.length === 0
        ) {
            return [];
        }

        return tenancies.active.occupants
            .filter((occupant) => occupant.name !== null)
            .map((occupant) => occupant.name);
    }, [tenancies]) as string[];

    if (tenancies === undefined || isLoading) {
        return (
            <VStack align="left" spacing={6}>
                <Box>
                    <FormLabel>1. Choose tenant</FormLabel>
                    <Skeleton p={6} h={2} w="full" />
                </Box>
                <Box>
                    <FormLabel>2. Add notes about the call</FormLabel>
                    <Skeleton p={6} h={TINYMCE_CONTAINER_HEIGHT} w="full" />
                </Box>
            </VStack>
        );
    }

    function onSubmit(formValues: CallTenantFormValues) {
        const callNotes = [
            `Called occupant: ${formValues.name}`,
            `Notes: ${formValues.notes}`,
        ].join('\n\n');

        onTenantCalled(callNotes);
    }

    return (
        <VStack align="left">
            <VStack align="left" spacing={6}>
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState }) => (
                        <FormControl isInvalid={fieldState.invalid}>
                            <FormLabel>1. Choose tenant</FormLabel>
                            <RadioGroup
                                value={field.value}
                                onChange={(name) => field.onChange(name)}
                            >
                                {
                                    occupantContactNames.length === 0 && (
                                        <Alert
                                            status="error"
                                            title="There are no tenants that can be contacted."
                                        />
                                    )
                                }
                                <Stack direction={['column', null, 'row']} spacing={[1, null, 8]}>
                                    {
                                        occupantContactNames.map((name) => (
                                            <Stack key={name} direction="row" spacing={4}>
                                                <Radio value={name}>
                                                    {name}
                                                </Radio>
                                            </Stack>
                                        ))
                                    }
                                </Stack>
                            </RadioGroup>
                            {
                                fieldState.error && (
                                    <FormErrorMessage>
                                        { fieldState.error.message}
                                    </FormErrorMessage>
                                )
                            }
                        </FormControl>
                    )}
                />

                <Controller
                    control={control}
                    name="notes"
                    render={({ field, fieldState }) => (
                        <FormControl
                            isInvalid={fieldState.invalid}
                            isRequired
                        >
                            <FormLabel>2. Add notes about the call</FormLabel>
                            <Suspense fallback={<Skeleton height={TINYMCE_CONTAINER_HEIGHT} />}>
                                <TinyMCEEditorWithImageUpload
                                    containerHeight={TINYMCE_CONTAINER_HEIGHT}
                                    onStartUpload={() => {
                                        setIsUploadingImage(true);
                                    }}
                                    onFinishUpload={() => {
                                        setIsUploadingImage(false);
                                    }}
                                    onEditorChange={field.onChange}
                                    value={field.value}
                                />
                            </Suspense>
                            {
                                fieldState.error && (
                                    <FormErrorMessage>
                                        { fieldState.error.message}
                                    </FormErrorMessage>
                                )
                            }
                        </FormControl>
                    )}
                />
            </VStack>
            <HStack justifyContent="end" pt={6}>
                <Button
                    variant="solid"
                    colorScheme="secondary"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                    isDisabled={isUploadingImage || !isValid}
                >
                    {confirmButtonText ?? 'Confirm'}
                </Button>
            </HStack>
        </VStack>
    );
}
